// Filtres de la map (BI-679) : fonctions pures de filtrage, de comptage et de
// (de)serialisation de la query string. Aucune dependance React.
import { CATEGORIES_AFFICHAGE, categorieFlux, estKiwee, filenameJob } from "./categoriesFlux";

export const CLES_CATEGORIES = ["get", "put", "import", "extractions", "autres", "kiwee"];
// "sans" = environnement actif sans aucun traitement rattache (ex-"jamais execute").
// "serveurs" (BI-682) = carte « Serveurs non remontes » : pas un statut de traitement, il
// affiche les noeuds crees cote front a partir de /api/logs/unreported-servers.
export const CLES_STATUTS = ["ok", "warning", "erreur", "grise", "encours", "sans", "serveurs"];
// Alias lus a l'arrivee (anciens liens) : jamais -> sans.
export const ALIAS_STATUTS = { jamais: "sans" };
// Anciennes vues (categorie) : retombent sur la vue par defaut (valeur inconnue).
export const CLES_VUES = ["treemap", "hex", "matrice", "tableau", "3d"];
export const VUE_DEFAUT = "treemap";

export const LIBELLES_STATUTS = {
  ok: "OK",
  warning: "Warning",
  erreur: "Erreur",
  grise: "Grisé / expiré",
  encours: "En cours",
  sans: "Sans traitement",
  serveurs: "Serveurs non remontés",
};

export const FILTRES_VIDES = { categories: [], statuts: [], q: "", vue: VUE_DEFAUT };

/* --------------------------------- Texte --------------------------------- */

export function normaliserTexte(s) {
  return String(s === null || s === undefined ? "" : s)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

/* ------------------------------- Statuts job ------------------------------ */

// Cles de statut d'un job (statut du dernier build, tel que calcule par la map, H8).
export function statutsJob(job) {
  const keys = [];
  if (!job) return keys;
  if (job.expired) {
    keys.push("grise");
  } else {
    const s = String(job.status_id);
    if (s === "0") keys.push("ok");
    else if (s === "1") keys.push("warning");
    else if (s === "2") keys.push("erreur");
    else if (s === "3") keys.push("grise");
    else if (s === "4") keys.push("sans");
  }
  if (job.running) keys.push("encours");
  return keys;
}

export function categorieJob(job) {
  return categorieFlux(filenameJob(job));
}

/* ------------------------------ Filtres actifs ----------------------------- */

const norm = (f) => ({
  categories: Array.isArray(f && f.categories) ? f.categories : [],
  statuts: Array.isArray(f && f.statuts) ? f.statuts : [],
  q: normaliserTexte(f && f.q),
});

export function filtresActifs(f) {
  const n = norm(f);
  return n.categories.length > 0 || n.statuts.length > 0 || n.q !== "";
}

/* ------------------------------ Correspondance ----------------------------- */

// ctx : { group, server, env } (noms des ancetres, optionnels).
function texteJob(job, ctx) {
  return normaliserTexte(
    [
      ctx && ctx.group,
      ctx && ctx.server,
      ctx && ctx.env,
      job.group_name,
      job.server_name,
      job.code_client_cti,
      job.name,
      job.filename,
    ]
      .filter(Boolean)
      .join(" ")
  );
}

function correspondCategories(job, categories) {
  if (!categories.length) return true;
  const cat = categorieJob(job).toLowerCase();
  if (categories.includes(cat)) return true;
  return categories.includes("kiwee") && estKiwee(filenameJob(job));
}

function correspondStatuts(job, statuts) {
  if (!statuts.length) return true;
  return statutsJob(job).some((k) => statuts.includes(k));
}

/*
 * dimensions : "categories" | "statuts" | "q" (permet d'exclure la famille categorie
 * pour les compteurs). Par defaut toutes.
 */
export function jobCorrespond(job, filtres, ctx, ignorer = []) {
  const n = norm(filtres);
  if (!ignorer.includes("categories") && !correspondCategories(job, n.categories)) return false;
  if (!ignorer.includes("statuts") && !correspondStatuts(job, n.statuts)) return false;
  if (!ignorer.includes("q") && n.q && !texteJob(job, ctx).includes(n.q)) return false;
  return true;
}

/* ------------------------------- Recalcul env ------------------------------ */

const niveau = (liste) =>
  liste.reduce((prev, cur) => (Number(cur) > Number(prev) ? String(cur) : prev), "0");

function recalculerEnv(env, jobs) {
  return {
    ...env,
    jobs,
    status_id: niveau(jobs.map((j) => j.status_id)),
    expired: jobs.some((j) => j.expired),
    running: jobs.some((j) => j.running),
    errors: jobs.filter((j) => String(j.status_id) === "2").length,
  };
}

// Env sans aucun job (sans traitement) : conserve seulement si les filtres le permettent.
function envVideRetenu(env, n, ctx) {
  if (n.categories.length && !n.categories.includes("autres")) return false;
  if (n.statuts.length && !n.statuts.includes("sans")) return false;
  if (n.q) {
    const t = normaliserTexte(
      [ctx.group, ctx.server, env.name, env.code_client_cti, env.group_name, env.server_name]
        .filter(Boolean)
        .join(" ")
    );
    if (!t.includes(n.q)) return false;
  }
  return true;
}

function texteNoeudNr(env, ctx) {
  return normaliserTexte(
    [ctx.group, ctx.server, env.name, env.code_client_cti, env.raison_sociale, env.group_name, env.server_name]
      .filter(Boolean)
      .join(" ")
  );
}

const estJob = (n) => n && n.type === "job";
const estEnv = (n) => n && (n.type === "client" || (n.jobs !== undefined && !n.type));

/*
 * Applique {categories, statuts, q} a un noeud de l'arbre formate (racine, groupe, serveur,
 * env, ou noeud de rendu {name, children}). Retourne un nouvel arbre ; statut/erreurs/expired/
 * running recalcules sur les seuls jobs retenus ; noeuds vides retires. Sans filtre actif,
 * retourne le noeud d'origine (meme reference). Retourne null si un noeud non racine est vide.
 */
export function filtrerArbre(node, filtres, ctx = {}) {
  if (!node || !filtresActifs(filtres)) return node;
  const n = norm(filtres);
  const res = filtrerNoeud(node, filtres, n, ctx, true);
  return res || { ...node, children: [] };
}

function filtrerNoeud(node, filtres, n, ctx, estRacine) {
  if (node && node.nonRemonte && estEnv(node)) {
    // noeud non remonte (BI-682) : ni categorie ni statut ne le masquent, la recherche oui.
    return !n.q || texteNoeudNr(node, ctx).includes(n.q) ? node : null;
  }
  if (estEnv(node) && Array.isArray(node.jobs)) {
    const sub = { ...ctx, env: node.name };
    const jobs = node.jobs.filter((j) => jobCorrespond(j, filtres, sub));
    if (!jobs.length) {
      return node.jobs.length === 0 && envVideRetenu(node, n, ctx) ? node : null;
    }
    return recalculerEnv(node, jobs);
  }
  if (!Array.isArray(node.children)) return estRacine ? node : null;

  const sub = { ...ctx };
  if (node.type === "group") sub.group = node.name;
  else if (node.type === "server") sub.server = node.name;

  let enfants;
  if (node.children.some(estJob) || node.children.length === 0) {
    enfants = node.children.filter((j) => !estJob(j) || jobCorrespond(j, filtres, sub));
  } else {
    enfants = node.children
      .map((c) => filtrerNoeud(c, filtres, n, sub, false))
      .filter(Boolean);
  }
  if (!enfants.length) return estRacine ? { ...node, children: [] } : null;

  const out = { ...node, children: enfants };
  if (node.type === "server") {
    out.level = niveau(enfants.map((e) => e.status_id));
    out.errorCount = enfants.filter((e) => String(e.status_id) === "2").length;
  } else if (node.type === "group") {
    const niv = niveau(enfants.map((e) => (e.type === "server" ? e.level : e.status_id)));
    out.status_id = niv;
    out.errors = enfants.reduce(
      (acc, e) =>
        acc + (e.type === "server" ? e.errorCount : String(e.status_id) === "2" ? 1 : 0),
      0
    );
  }
  return out;
}

/* ------------------------------ Collecte / compteurs ----------------------- */

// Liste a plat des jobs d'un noeud avec leur contexte : [{ job, group, server, env }].
export function collecterJobs(node, ctx = {}, out = []) {
  if (!node) return out;
  if (estEnv(node) && Array.isArray(node.jobs)) {
    node.jobs.forEach((job) =>
      out.push({
        job,
        group: ctx.group || node.group_name || job.group_name || "",
        server: ctx.server || node.server_name || job.server_name || "",
        env: node.name || "",
      })
    );
    return out;
  }
  if (!Array.isArray(node.children)) return out;
  const sub = { ...ctx };
  if (node.type === "group") sub.group = node.name;
  if (node.type === "server") sub.server = node.name;
  node.children.forEach((c) => {
    if (estJob(c)) {
      out.push({
        job: c,
        group: sub.group || c.group_name || "",
        server: sub.server || c.server_name || "",
        env: sub.env || c.code_client_cti || node.name || "",
      });
    } else {
      collecterJobs(c, sub, out);
    }
  });
  return out;
}

/*
 * Compteurs par categorie sur le perimetre courant (noeud + filtres statut et recherche,
 * hors filtre categorie) : { GET: {total, erreurs}, ..., KIWEE: {...} }.
 */
export function compterParCategorie(node, filtres) {
  const compteurs = {
    EXTRACTIONS: { total: 0, erreurs: 0 },
    GET: { total: 0, erreurs: 0 },
    PUT: { total: 0, erreurs: 0 },
    IMPORT: { total: 0, erreurs: 0 },
    AUTRES: { total: 0, erreurs: 0 },
    KIWEE: { total: 0, erreurs: 0 },
  };
  collecterJobs(node).forEach(({ job, group, server, env }) => {
    if (!jobCorrespond(job, filtres, { group, server, env }, ["categories"])) return;
    const err = String(job.status_id) === "2" ? 1 : 0;
    const cat = categorieJob(job);
    compteurs[cat].total += 1;
    compteurs[cat].erreurs += err;
    if (estKiwee(filenameJob(job))) {
      compteurs.KIWEE.total += 1;
      compteurs.KIWEE.erreurs += err;
    }
  });
  return compteurs;
}

/* ------------------------------ Query string ------------------------------ */

const listeValide = (valeur, autorises) => {
  const vues = new Set();
  String(valeur || "")
    .split(",")
    .map((v) => normaliserTexte(v))
    .map((v) => ALIAS_STATUTS[v] || v)
    .forEach((v) => {
      if (autorises.includes(v)) vues.add(v);
    });
  return autorises.filter((v) => vues.has(v));
};

// "?cat=get,put&statut=erreur&q=x&vue=tableau" -> filtres ; valeurs inconnues ignorees
// (alias : statut=jamais -> sans).
export function parseFiltresQuery(search) {
  let params;
  try {
    params = new URLSearchParams(String(search || ""));
  } catch (e) {
    return { ...FILTRES_VIDES };
  }
  const vue = normaliserTexte(params.get("vue"));
  return {
    categories: listeValide(params.get("cat"), CLES_CATEGORIES),
    statuts: listeValide(params.get("statut"), CLES_STATUTS),
    q: String(params.get("q") || "").trim(),
    vue: CLES_VUES.includes(vue) ? vue : VUE_DEFAUT,
  };
}

// filtres -> "?cat=...&..." ("" si rien a ecrire ; valeurs par defaut omises).
export function serialiserFiltresQuery(filtres) {
  const f = filtres || {};
  const params = new URLSearchParams();
  const cats = listeValide((f.categories || []).join(","), CLES_CATEGORIES);
  const stats = listeValide((f.statuts || []).join(","), CLES_STATUTS);
  if (cats.length) params.set("cat", cats.join(","));
  if (stats.length) params.set("statut", stats.join(","));
  if (f.q && String(f.q).trim()) params.set("q", String(f.q).trim());
  if (f.vue && f.vue !== VUE_DEFAUT && CLES_VUES.includes(f.vue)) params.set("vue", f.vue);
  const s = params.toString().replace(/%2C/g, ",");
  return s ? "?" + s : "";
}

/*
 * Presentation "Treemap par categorie" (H9) : au niveau env uniquement (enfants = jobs),
 * regroupe les jobs en sous-cadres {type:"categorie", name:"GET (12)"} ; sinon noeud inchange.
 */
export function regrouperParCategorie(node) {
  if (!node || !Array.isArray(node.children) || !node.children.length) return node;
  if (!node.children.every(estJob)) return node;
  const groupes = CATEGORIES_AFFICHAGE
    .map((cat) => {
      const jobs = node.children.filter((j) => categorieJob(j) === cat);
      return {
        name: `${cat} (${jobs.length})`,
        type: "categorie",
        categorie: cat,
        errors: jobs.filter((j) => String(j.status_id) === "2").length,
        children: jobs,
      };
    })
    .filter((g) => g.children.length);
  return { ...node, children: groupes };
}

/* ------------------------- Agregats pour les vues (BI-679) ------------------------- */

// Statut d'affichage d'un traitement / d'un environnement : couleur, libelle, poids (gravite).
export const STATUTS_META = {
  erreur: { libelle: "Erreur", couleur: "#e5484d", poids: 5 },
  warning: { libelle: "Warning", couleur: "#f5a623", poids: 4 },
  grise: { libelle: "Grisé (expiré)", couleur: "#9aa5b1", poids: 3 },
  ok: { libelle: "OK", couleur: "#3fb27f", poids: 1 },
  sans: { libelle: "Sans traitement", couleur: "#c9d1db", poids: 0 },
  // BI-682 (H5) : violet ardoise + hachure fine (cf. MapVues.css / treemap), hors echelle 0-4.
  non_remonte: { libelle: "Non remonté", couleur: "#5b4b8a", poids: 0.5 },
};
export const STATUT_NON_REMONTE = "non_remonte";
export const COULEUR_EN_COURS = "#3b82f6";
// Couleurs des pastilles d'historique, indexees par status_id du build (0 ok, 1 warning, 2 erreur, 3 inconnu).
export const COULEURS_HISTORIQUE = ["#3fb27f", "#f5a623", "#e5484d", "#d5dbe2"];

// Statut principal d'un traitement : expire et pas en cours -> grise ; sinon d'apres status_id.
export function statutPrincipal(job) {
  if (!job) return "sans";
  const s = String(job.status_id);
  if (s === "4") return "sans";
  if (job.expired && !job.running) return "grise";
  if (s === "2") return "erreur";
  if (s === "1") return "warning";
  if (s === "3") return "grise";
  return "ok";
}

// Pire statut d'une liste de cles de statut ("sans" si la liste est vide).
export function pireStatut(cles) {
  let pire = "sans";
  let poids = -1;
  (Array.isArray(cles) ? cles : []).forEach((k) => {
    const m = STATUTS_META[k];
    if (m && m.poids > poids) {
      poids = m.poids;
      pire = k;
    }
  });
  return pire;
}

/*
 * Agregat d'un environnement : { key, group, server, env, code, vague, jobs, statut, poids,
 * erreurs, running, sans }. "sans" = aucun traitement rattache (ligne factice jamais_execute,
 * non conservee dans jobs par le formatage).
 */
export function agregerEnv(infos, jobs) {
  const liste = Array.isArray(jobs) ? jobs : [];
  const nonRemonte = !!infos.nonRemonte;
  const statut = nonRemonte
    ? STATUT_NON_REMONTE
    : liste.length
    ? pireStatut(liste.map(statutPrincipal))
    : "sans";
  return {
    key: [infos.group, infos.server, infos.code || infos.env, infos.finess]
      .filter((x, i) => i < 3 || (x !== undefined && x !== null && x !== ""))
      .join("|"),
    group: infos.group || "",
    server: infos.server || "",
    env: infos.env || "",
    code: infos.code || "",
    vague: infos.vague || "",
    jobs: liste,
    statut,
    poids: STATUTS_META[statut].poids,
    erreurs: liste.filter((j) => statutPrincipal(j) === "erreur").length,
    running: liste.some((j) => j && j.running),
    sans: !nonRemonte && liste.length === 0,
    nonRemonte,
    dernierBuild: nonRemonte ? infos.dernierBuild || null : undefined,
  };
}

/*
 * Liste a plat des environnements d'un noeud (racine, groupe, serveur, env ou noeud de rendu
 * {name, children: jobs}). Les environnements sans traitement sont inclus.
 */
export function collecterEnvs(node, ctx = {}, out = []) {
  if (!node) return out;
  if (estEnv(node) && Array.isArray(node.jobs)) {
    out.push(
      agregerEnv(
        {
          group: ctx.group || node.group_name,
          server: ctx.server || node.server_name,
          env: node.name,
          code: node.code_client_cti,
          vague: node.vague_name,
          finess: node.finess_id,
          nonRemonte: !!node.nonRemonte,
          dernierBuild: node.dernierBuild,
        },
        node.jobs
      )
    );
    return out;
  }
  if (!Array.isArray(node.children) || !node.children.length) return out;
  if (node.children.every(estJob)) {
    // noeud de rendu d'un environnement : {name, children: jobs}
    const j0 = node.children[0];
    out.push(
      agregerEnv(
        {
          group: ctx.group || j0.group_name,
          server: ctx.server || j0.server_name,
          env: node.name || j0.code_client_cti,
          code: j0.code_client_cti,
          vague: j0.vague_name,
          finess: j0.finess_id,
        },
        node.children
      )
    );
    return out;
  }
  const sub = { ...ctx };
  if (node.type === "group") sub.group = node.name;
  if (node.type === "server") sub.server = node.name;
  node.children.forEach((c) => collecterEnvs(c, sub, out));
  return out;
}

/*
 * Regroupe des environnements par groupe : [{ group, envs, statut, poids, erreurs, nb }],
 * groupes en anomalie en tete (poids, erreurs, nom).
 */
export function agregerGroupes(envs) {
  const m = new Map();
  // cles React uniques meme si deux envs partagent groupe/serveur/code (suffixe en dernier recours)
  const vus = new Map();
  (Array.isArray(envs) ? envs : []).forEach((e0) => {
    const n = vus.get(e0.key) || 0;
    vus.set(e0.key, n + 1);
    const e = n ? { ...e0, key: `${e0.key}#${n}` } : e0;
    let g = m.get(e.group);
    if (!g) {
      g = { group: e.group, envs: [], poids: -1, statut: "sans", erreurs: 0, nb: 0 };
      m.set(e.group, g);
    }
    g.envs.push(e);
    g.nb += e.jobs.length;
    g.erreurs += e.erreurs;
    if (e.poids > g.poids) {
      g.poids = e.poids;
      g.statut = e.statut;
    }
  });
  return [...m.values()]
    .map((g) => ({
      ...g,
      envs: [...g.envs].sort((a, b) => b.poids - a.poids || cmpTexte(a.env, b.env)),
    }))
    .sort((a, b) => b.poids - a.poids || b.erreurs - a.erreurs || cmpTexte(a.group, b.group));
}

const cmpTexte = (a, b) => String(a || "").localeCompare(String(b || ""), "fr", { numeric: true });

// Traitements d'un environnement dans une categorie (Kiwee n'est pas une categorie).
export function jobsDeCategorie(env, categorie) {
  return ((env && env.jobs) || []).filter((j) => categorieJob(j) === categorie);
}

// Traitements d'un environnement groupes par categorie (ordre d'affichage, categories vides omises).
export function jobsParCategorie(env, seulement) {
  return CATEGORIES_AFFICHAGE.filter((c) => !seulement || c === seulement)
    .map((c) => ({ categorie: c, jobs: jobsDeCategorie(env, c) }))
    .filter((x) => x.jobs.length);
}

// Resume par categorie d'un environnement : [{ categorie, jobs (nombre), erreurs }].
export function resumeCategories(env) {
  return jobsParCategorie(env).map(({ categorie, jobs }) => ({
    categorie,
    jobs: jobs.length,
    erreurs: jobs.filter((j) => statutPrincipal(j) === "erreur").length,
  }));
}

/*
 * Etat d'un environnement pour une categorie : { n, statut, erreurs, running } ou null si
 * aucun traitement de cette categorie (cellule de la matrice, lentille hexagonale).
 */
export function statutCategorie(env, categorie) {
  const jobs = jobsDeCategorie(env, categorie);
  if (!jobs.length) return null;
  return {
    n: jobs.length,
    statut: pireStatut(jobs.map(statutPrincipal)),
    erreurs: jobs.filter((j) => statutPrincipal(j) === "erreur").length,
    running: jobs.some((j) => j.running),
  };
}

/*
 * Chiffres des cartes d'en-tete sur le perimetre courant. Les filtres categorie et recherche
 * s'appliquent, PAS le filtre statut (sinon les cartes des autres statuts tomberaient a zero) :
 * { total, erreur, warning, grise, encours, sans }. Comptes alignes sur le filtre de statut
 * (statutsJob) : cliquer une carte donne exactement ce nombre de traitements.
 */
export function calculerKpi(node, filtres) {
  const kpi = { total: 0, erreur: 0, warning: 0, grise: 0, encours: 0, sans: 0 };
  const arbre = filtrerArbre(node, { ...(filtres || {}), statuts: [] });
  collecterJobs(arbre).forEach(({ job }) => {
    kpi.total += 1;
    const cles = statutsJob(job);
    ["erreur", "warning", "grise", "encours"].forEach((k) => {
      if (cles.includes(k)) kpi[k] += 1;
    });
  });
  kpi.sans = collecterEnvs(arbre).filter((e) => e.sans).length;
  return kpi;
}

/* ------------------- Serveurs non remontes (BI-682) ------------------- */

// Reponse de /api/logs/unreported-servers -> liste valide (entrees invalides filtrees).
export function nettoyerServeursNonRemontes(data) {
  return (Array.isArray(data) ? data : []).filter(
    (s) => s && typeof s === "object" && s.server_id !== undefined && s.server_id !== null
  );
}

/*
 * Arbre front racine -> groupe -> serveur -> env des serveurs non remontes (H7 : un serveur
 * apparait sous chaque groupe de ses envs ; un env n'est liste qu'une fois). Les noeuds
 * portent nonRemonte:true et le statut front "non_remonte" ; aucun job.
 */
export function construireArbreNonRemontes(liste) {
  const groupes = new Map();
  const envsVus = new Set();
  nettoyerServeursNonRemontes(liste).forEach((s) => {
    const nomServeur = s.server_name || s.server_hostname || String(s.server_id);
    (Array.isArray(s.envs) ? s.envs : []).forEach((e) => {
      if (!e) return;
      const cleEnv = e.finess_id !== undefined && e.finess_id !== null ? e.finess_id : `${e.group_id}|${e.code_client_cti}`;
      if (envsVus.has(cleEnv)) return;
      envsVus.add(cleEnv);
      const nomGroupe = e.group_name || "";
      let g = groupes.get(e.group_id + "|" + nomGroupe);
      if (!g) {
        g = {
          name: nomGroupe,
          oid: e.group_id,
          key: `nr-g-${e.group_id}`,
          type: "group",
          type_id: 1,
          status_id: "0",
          errors: 0,
          nonRemonte: true,
          children: [],
          serveurs: new Map(),
        };
        groupes.set(e.group_id + "|" + nomGroupe, g);
      }
      let srv = g.serveurs.get(s.server_id);
      if (!srv) {
        srv = {
          name: nomServeur,
          server_id: s.server_id,
          server_name: nomServeur,
          group_name: nomGroupe,
          group_id: e.group_id,
          key: `nr-s-${e.group_id}-${s.server_id}`,
          type: "server",
          type_id: 2,
          level: "0",
          errorCount: 0,
          nonRemonte: true,
          dernierBuild: s.last_build_at || null,
          children: [],
        };
        g.serveurs.set(s.server_id, srv);
        g.children.push(srv);
      }
      srv.children.push({
        name: e.code_client_cti,
        code_client_cti: e.code_client_cti,
        raison_sociale: e.raison_sociale,
        finess_id: e.finess_id,
        group_id: e.group_id,
        group_name: nomGroupe,
        server_id: s.server_id,
        server_name: nomServeur,
        key: `nr-e-${cleEnv}`,
        type: "client",
        type_id: 3,
        value: 1,
        status_id: "0",
        expired: false,
        running: false,
        errors: 0,
        jobs: [],
        latest_integrity: true,
        nonRemonte: true,
        dernierBuild: s.last_build_at || null,
      });
    });
  });
  return {
    name: "Racine",
    children: [...groupes.values()].map(({ serveurs, ...g }) => g),
  };
}

// Nombre de serveurs distincts d'un arbre non remonte (apres recherche texte q).
export function compterServeursNonRemontes(arbre, q = "") {
  const filtre = filtrerArbre(arbre, { categories: [], statuts: [], q });
  const ids = new Set();
  const parcourir = (n) => {
    if (!n || !Array.isArray(n.children)) return;
    n.children.forEach((c) => {
      if (c.type === "server") ids.add(c.server_id);
      else parcourir(c);
    });
  };
  parcourir(filtre);
  return ids.size;
}

// Groupe d'environnements normaux sans niveau serveur -> noeuds serveur (pour la fusion).
function avecNiveauServeur(groupe) {
  const enfants = Array.isArray(groupe.children) ? groupe.children : [];
  if (!enfants.length || !enfants.every((c) => c && c.type !== "server")) return groupe;
  const parServeur = new Map();
  enfants.forEach((e) => {
    const nom = e.server_name || e.server_hostname || "";
    if (!parServeur.has(nom)) parServeur.set(nom, []);
    parServeur.get(nom).push(e);
  });
  return {
    ...groupe,
    children: [...parServeur.entries()].map(([nom, envs]) => ({
      name: nom,
      group_name: groupe.name,
      type: "server",
      type_id: 2,
      level: niveau(envs.map((e) => e.status_id)),
      errorCount: envs.filter((e) => String(e.status_id) === "2").length,
      children: envs,
    })),
  };
}

// Union (H6) de l'arbre des logs et de l'arbre non remonte, fusionnes par nom de groupe.
export function fusionnerArbres(logs, nr) {
  if (!logs || !Array.isArray(logs.children)) return nr;
  const nrGroupes = (nr && nr.children) || [];
  const parNom = new Map(nrGroupes.map((g) => [g.name, g]));
  const utilises = new Set();
  const groupes = logs.children.map((g) => {
    const autre = parNom.get(g.name);
    if (!autre) return g;
    utilises.add(g.name);
    const base = avecNiveauServeur(g);
    return { ...base, children: [...(base.children || []), ...autre.children] };
  });
  nrGroupes.forEach((g) => {
    if (!utilises.has(g.name)) groupes.push(g);
  });
  return { ...logs, children: groupes };
}

// "dernier build : 27/10/2026 19:28" ou "jamais remonté" (formateur de date injecte).
export function texteDernierBuild(dernierBuild, formater) {
  const d = dernierBuild ? (formater ? formater(dernierBuild) : String(dernierBuild)) : "";
  return d ? `dernier build : ${d}` : "jamais remonté";
}
